// SA-7 — OEE-lite по станкам (фаза 4)

import { useCallback, useEffect, useMemo, useState } from 'react'

import { pocketbaseErrorMessage } from '../../lib/pocketbaseErrorMessage'
import {
  COL_MACHINE_EVENTS,
  COL_MACHINES,
  COL_SCHEDULE_SLOTS,
} from '../../config/collections'
import { Alert, PageHeader, PageShell } from '../../components/ui'
import { ExportCsvButton } from '../../components/ExportCsvButton'
import { ShiftFilterSelect } from '../../components/ShiftFilterSelect'
import { formInputProps } from '../../components/ui/FormField'
import type { CsvColumn } from '../../lib/exportCsv'
import { useAssignedShiftFilter } from '../../hooks/useAssignedShiftFilter'
import { machineTypeLabel } from '../../lib/machines'
import { totalDowntimeHours } from '../../lib/machineEvents'
import { computeOee, machineOeeForPeriod } from '../../lib/oeeCalculations'
import { fetchCalendarMap } from '../../lib/loadProductionCalendar'
import { countPlantWorkingDaysInRange } from '../../lib/productionCalendar'
import { pb } from '../../pocketbase'
import type { MachineEventRecord, MachineRecord, ScheduleSlotRecord } from '../../types/records'
import {
  btn,
  cn,
  labelClass,
  tableCellClass,
  tableClass,
  tableHeadClass,
  tableLastRowClass,
  tableRowHoverClass,
  tableWrapClass,
  toolbarClass,
} from '../../lib/ui'

type MachineOeeRow = {
  machine: MachineRecord
  plannedOutput: number
  actualOutput: number
  defectQty: number
  oee: ReturnType<typeof machineOeeForPeriod>
}

function formatPct(value: number): string {
  return `${value}%`
}

export function OeePage() {
  const { shiftId, setShiftId, shifts, canAllShifts, shiftLocked, appendShiftFilter } =
    useAssignedShiftFilter()
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date()
    d.setDate(d.getDate() - 7)
    return d.toISOString().slice(0, 10)
  })
  const [toDate, setToDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [machines, setMachines] = useState<MachineRecord[]>([])
  const [slots, setSlots] = useState<ScheduleSlotRecord[]>([])
  const [events, setEvents] = useState<MachineEventRecord[]>([])
  const [calendarMap, setCalendarMap] = useState(() => new Map<string, { isWorking: boolean }>())

  const rangeStart = `${fromDate} 00:00:00.000Z`
  const rangeEnd = `${toDate} 23:59:59.999Z`

  const load = useCallback(async () => {
    setErr('')
    setLoading(true)
    try {
      const [machineList, slotList, eventList, calMap] = await Promise.all([
        pb.collection(COL_MACHINES).getFullList<MachineRecord>({ sort: 'type,sort,number' }),
        pb.collection(COL_SCHEDULE_SLOTS).getFullList<ScheduleSlotRecord>({
          filter: appendShiftFilter(
            `plannedStart >= "${rangeStart}" && plannedStart <= "${rangeEnd}" && status != "cancelled"`,
          ),
        }),
        pb.collection(COL_MACHINE_EVENTS).getFullList<MachineEventRecord>({
          filter: `startAt >= "${rangeStart}" && startAt <= "${rangeEnd}"`,
        }),
        fetchCalendarMap(fromDate, toDate),
      ])
      setMachines(machineList)
      setSlots(slotList)
      setEvents(eventList)
      setCalendarMap(calMap)
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка загрузки OEE'))
    } finally {
      setLoading(false)
    }
  }, [rangeStart, rangeEnd, fromDate, toDate, appendShiftFilter])

  useEffect(() => {
    void load()
  }, [load])

  const rows = useMemo((): MachineOeeRow[] => {
    const days = Math.max(1, countPlantWorkingDaysInRange(fromDate, toDate, calendarMap))

    return machines.map((machine) => {
      const machineSlots = slots.filter((s) => s.machine === machine.id)
      const machineEvents = events.filter((e) => e.machine === machine.id)
      const availableHours = (Number(machine.working_hours_per_day) || 0) * days

      const plannedOutput = machineSlots.reduce((sum, s) => sum + (Number(s.plannedQuantity) || 0), 0)
      const actualOutput = machineSlots.reduce((sum, s) => sum + (Number(s.actualQuantity) || 0), 0)
      const defectQty = machineSlots.reduce((sum, s) => sum + (Number(s.defectQuantity) || 0), 0)

      return {
        machine,
        plannedOutput,
        actualOutput,
        defectQty,
        oee: machineOeeForPeriod(machineSlots, machineEvents, availableHours, rangeStart, rangeEnd),
      }
    })
  }, [machines, slots, events, fromDate, toDate, rangeStart, rangeEnd, calendarMap])

  const totals = useMemo(() => {
    const days = Math.max(1, countPlantWorkingDaysInRange(fromDate, toDate, calendarMap))
    const availableHours = machines.reduce((sum, m) => sum + (Number(m.working_hours_per_day) || 0) * days, 0)
    const plannedOutput = rows.reduce((s, r) => s + r.plannedOutput, 0)
    const actualOutput = rows.reduce((s, r) => s + r.actualOutput, 0)
    const defectQty = rows.reduce((s, r) => s + r.defectQty, 0)
    return computeOee({
      availableHours,
      downtimeHours: totalDowntimeHours(events, rangeStart, rangeEnd),
      plannedOutput,
      actualOutput,
      defectQty,
    })
  }, [rows, machines, events, fromDate, toDate, rangeStart, rangeEnd, calendarMap])

  const oeeCsvColumns: CsvColumn<MachineOeeRow>[] = useMemo(
    () => [
      {
        header: 'Станок',
        value: (r) => `${machineTypeLabel(r.machine.type)} №${r.machine.number}`,
      },
      { header: 'OEE', value: (r) => formatPct(r.oee.oee) },
      { header: 'Availability', value: (r) => formatPct(r.oee.availability) },
      { header: 'Performance', value: (r) => formatPct(r.oee.performance) },
      { header: 'Quality', value: (r) => formatPct(r.oee.quality) },
      { header: 'План', value: (r) => r.plannedOutput },
      { header: 'Факт', value: (r) => r.actualOutput },
      { header: 'Брак', value: (r) => r.defectQty },
    ],
    [],
  )

  return (
    <PageShell>
      <PageHeader
        title="OEE по станкам"
        lead="Availability × Performance × Quality за выбранный период. Простои — из событий станков, качество — с учётом брака."
      />
      {err && <Alert kind="err">{err}</Alert>}

      <div className={toolbarClass}>
        <label className="flex flex-col gap-1">
          <span className={labelClass}>С</span>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} {...formInputProps('max-w-40')} />
        </label>
        <label className="flex flex-col gap-1">
          <span className={labelClass}>По</span>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} {...formInputProps('max-w-40')} />
        </label>
        <button type="button" className={btn()} onClick={() => void load()} disabled={loading}>
          Обновить
        </button>
        <ShiftFilterSelect
          shiftId={shiftId}
          shifts={shifts}
          canAllShifts={canAllShifts}
          locked={shiftLocked}
          onChange={setShiftId}
        />
        {rows.length > 0 && (
          <ExportCsvButton filename={`oee-${fromDate}-${toDate}`} rows={rows} columns={oeeCsvColumns} />
        )}
      </div>

      {loading ? (
        <p className="text-sm text-su-text">
          <em>Загрузка…</em>
        </p>
      ) : (
        <>
          <div className="mb-4 rounded-su-lg border border-su-border bg-su-surface-muted px-4 py-3 text-sm">
            <strong className="text-su-text-strong">Итого OEE:</strong> {formatPct(totals.oee)}{' '}
            <span className="text-su-text">
              (A {formatPct(totals.availability)} · P {formatPct(totals.performance)} · Q {formatPct(totals.quality)})
            </span>
          </div>

          <div className={cn(tableWrapClass, tableLastRowClass)}>
            <table className={tableClass}>
              <thead>
                <tr>
                  <th className={tableHeadClass}>Станок</th>
                  <th className={tableHeadClass}>A</th>
                  <th className={tableHeadClass}>P</th>
                  <th className={tableHeadClass}>Q</th>
                  <th className={tableHeadClass}>OEE</th>
                  <th className={tableHeadClass}>План</th>
                  <th className={tableHeadClass}>Факт</th>
                  <th className={tableHeadClass}>Брак</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr className={tableRowHoverClass}>
                    <td className={tableCellClass} colSpan={8}>
                      <em>Нет станков</em>
                    </td>
                  </tr>
                )}
                {rows.map((row) => (
                  <tr key={row.machine.id} className={tableRowHoverClass}>
                    <td className={tableCellClass}>
                      {machineTypeLabel(row.machine.type)} №{row.machine.number}
                    </td>
                    <td className={tableCellClass}>{formatPct(row.oee.availability)}</td>
                    <td className={tableCellClass}>{formatPct(row.oee.performance)}</td>
                    <td className={tableCellClass}>{formatPct(row.oee.quality)}</td>
                    <td className={cn(tableCellClass, 'font-semibold')}>{formatPct(row.oee.oee)}</td>
                    <td className={tableCellClass}>{row.plannedOutput.toLocaleString('ru-RU')}</td>
                    <td className={tableCellClass}>{row.actualOutput.toLocaleString('ru-RU')}</td>
                    <td className={tableCellClass}>{row.defectQty.toLocaleString('ru-RU')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </PageShell>
  )
}
